import { CARRIER_TBN_NAME, resolveCarrier } from "../../constants/carriers.catalog";
import type { RateApiIssue, RateApiIssueCode, RateApiMode, RateApiRecord, RateApiStatus } from "../../api/rates.types";
import type { ClarifyQuestion, RateIssueCode, RateMode, RateRecord, RateStatus } from "./rates.model";
import { linerTermFromRate, linerTermLabels } from "./rates.model";

export const toUiIssueCode = (code: RateApiIssueCode): RateIssueCode => {
  if (code === "equipment") return "equip";
  if (code === "conflict") return "dup";

  return code;
};

export const toApiIssueCode = (code: RateIssueCode): RateApiIssueCode => {
  if (code === "equip") return "equipment";
  if (code === "dup") return "conflict";

  return code;
};

const toUiStatus = (status: RateApiStatus): RateStatus => {
  if (status === "draft" || status === "published") return "ready";
  if (status === "needs_info" || status === "expired") return "review";

  return "rejected";
};

const toUiMode = (mode: RateApiMode): RateMode => {
  if (mode === "sea") return "ocean";
  if (mode === "land") return "road";

  return "air";
};

const formatValidity = (from: string | null, until: string | null): string => {
  if (!from && !until) return "Not stated";

  const pretty = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  if (from && until) return `${pretty(from)} – ${pretty(until)}`;

  return pretty((until ?? from) as string);
};

export const mapRate = (rate: RateApiRecord): RateRecord => {
  const includesOriginThc = rate.includes_origin_thc ?? null;
  const includesDestinationThc = rate.includes_destination_thc ?? null;
  const liner = linerTermLabels(linerTermFromRate({ includesOriginThc, includesDestinationThc }));
  const resolved = resolveCarrier(rate.carrier_name ?? "");
  const carrierEmpty =
    !rate.carrier_name || !rate.carrier_name.trim() || /^unknown(?:\s+carrier)?$/i.test(rate.carrier_name.trim());
  const carrier = carrierEmpty ? CARRIER_TBN_NAME : resolved.name;
  const carrierCode = carrierEmpty ? "TBN" : resolved.code;

  return {
    id: String(rate.id),
    status: toUiStatus(rate.status),
    mode: toUiMode(rate.mode),
    service: rate.service,
    carrier,
    carrierCode,
    origin: rate.origin_raw ?? "—",
    destination: rate.destination_raw ?? "—",
    terms: liner.title,
    termsNote: liner.subtitle,
    equipment: rate.equipment ?? "—",
    transit: rate.transit_days == null ? "—" : `${rate.transit_days} days`,
    currency: rate.currency ?? "",
    amount: rate.carrier_amount,
    unit: rate.unit ?? "—",
    validity: formatValidity(rate.valid_from, rate.valid_until),
    transshipment: (rate.transshipment ?? []).filter(Boolean).join(", "),
    includesOriginThc,
    includesDestinationThc,
    issues: rate.issue_labels,
    issueCodes: rate.issue_codes.map(toUiIssueCode),
  };
};

export const mapQuestion = (issue: RateApiIssue): ClarifyQuestion => {
  const follow = issue.date_follow_up ?? undefined;
  const field = follow?.field;
  const guidedFollowUp =
    follow && field && field !== "validity" ? { field, label: follow.label, done: follow.done } : undefined;

  return {
    id: issue.id,
    key: toUiIssueCode(issue.code),
    ask: issue.question,
    options: issue.options,
    done: "",
    rateIds: issue.rate_ids,
    dateFollowUp: follow && (!field || field === "validity") ? follow : undefined,
    guidedFollowUp,
  };
};
